import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheckDouble,
  faXmark,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { TYPE_META, ago } from './notificationMeta';
import './Notificationbell.css';

/* =========================================================
   Notification bell (admin now, teacher dashboard later).
   - base: full endpoint, e.g. `${API}/api/admin/notifications`
   - token: the login token
   - withActivity: show the "Activity" tab (sign-ins) — admin only
   Times are saved in UTC (GETUTCDATE), so new Date(value) is correct.
   ========================================================= */
const POLL_MS = 45000;

const NotificationBell = ({ base, token, withActivity = false, allLink = null }) => {
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState('alerts');
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState({ alerts: 0, activity: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const call = useCallback(
    async (path = '', options = {}) => {
      const res = await fetch(`${base}${path}`, {
        ...options,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    [base, token]
  );

  // badge only (light request)
  const refreshCount = useCallback(async () => {
    try {
      const data = await call('/count');
      setUnread((u) => ({ ...u, alerts: data.unread || 0 }));
    } catch {
      /* keep the last number */
    }
  }, [call]);

  const loadList = useCallback(
    async (g) => {
      setLoading(true);
      setError('');
      try {
        const data = await call(`?group=${g}&limit=30`);
        setItems(Array.isArray(data.items) ? data.items : []);
        setUnread({ alerts: data.unreadAlerts || 0, activity: data.unreadActivity || 0 });
      } catch {
        setError("Couldn't load notifications.");
      } finally {
        setLoading(false);
      }
    },
    [call]
  );

  // check every 45s while the tab is visible, and when coming back to it
  useEffect(() => {
    if (!token || !base) return undefined;
    refreshCount();
    const tick = () => {
      if (document.visibilityState === 'visible') refreshCount();
    };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [token, base, refreshCount]);

  // load the list when opening / switching tab
  useEffect(() => {
    if (open) loadList(group);
  }, [open, group, loadList]);

  // close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = async (n) => {
    if (!n.IsRead) {
      setItems((list) => list.map((x) => (x.Id === n.Id ? { ...x, IsRead: true } : x)));
      setUnread((u) => ({ ...u, [group]: Math.max(0, u[group] - 1) }));
      call(`/${n.Id}/read`, { method: 'PUT' }).catch(() => {});
    }
    if (n.Link) {
      setOpen(false);
      navigate(n.Link);
    }
  };

  const readAll = async () => {
    setItems((list) => list.map((x) => ({ ...x, IsRead: true })));
    setUnread((u) => ({ ...u, [group]: 0 }));
    try {
      await call(`/read-all?group=${group}`, { method: 'PUT' });
    } catch {
      loadList(group);
    }
  };

  const badge = unread.alerts > 99 ? '99+' : unread.alerts;

  return (
    <div className="rmnb" ref={wrapRef}>
      <button
        type="button"
        className={`rmnb-btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={unread.alerts ? `Notifications, ${unread.alerts} unread` : 'Notifications'}
        title="Notifications"
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={faBell} />
        {unread.alerts > 0 && <span className="rmnb-badge">{badge}</span>}
      </button>

      {open && (
        <div className="rmnb-panel" role="dialog" aria-label="Notifications">
          <div className="rmnb-head">
            <strong>Notifications</strong>
            <div className="rmnb-head-actions">
              <button
                type="button"
                className="rmnb-readall"
                onClick={readAll}
                disabled={!unread[group]}
                title="Mark all as read"
                aria-label="Mark all as read"
              >
                <FontAwesomeIcon icon={faCheckDouble} />
                <span>Mark all read</span>
              </button>
              <button type="button" className="rmnb-close" onClick={() => setOpen(false)} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          {withActivity && (
            <div className="rmnb-tabs" role="tablist">
              {[
                ['alerts', 'Alerts'],
                ['activity', 'Sign-ins'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={group === key}
                  className={`rmnb-tab ${group === key ? 'is-on' : ''}`}
                  onClick={() => setGroup(key)}
                >
                  {label}
                  {unread[key] > 0 && <span className="rmnb-count">{unread[key] > 99 ? '99+' : unread[key]}</span>}
                </button>
              ))}
            </div>
          )}

          <div className="rmnb-list">
            {loading && items.length === 0 && <p className="rmnb-empty">Loading…</p>}
            {!loading && error && <p className="rmnb-empty is-error">{error}</p>}
            {!loading && !error && items.length === 0 && (
              <p className="rmnb-empty">{group === 'activity' ? 'No sign-ins yet.' : "You're all caught up."}</p>
            )}

            {items.map((n) => {
              const meta = TYPE_META[n.Type] || { icon: faBell, tone: 'blue' };
              return (
                <button
                  key={n.Id}
                  type="button"
                  className={`rmnb-item ${n.IsRead ? '' : 'is-unread'}`}
                  onClick={() => openItem(n)}
                >
                  <span className={`rmnb-icon tone-${meta.tone}`} aria-hidden="true">
                    <FontAwesomeIcon icon={meta.icon} />
                  </span>
                  <span className="rmnb-text">
                    <span className="rmnb-title">{n.Title}</span>
                    {n.Message && <span className="rmnb-msg">{n.Message}</span>}
                    <span className="rmnb-time">{ago(n.CreatedAt)}</span>
                  </span>
                  {!n.IsRead && <span className="rmnb-dot" aria-label="Unread" />}
                </button>
              );
            })}
          </div>

          {allLink && (
            <button
              type="button"
              className="rmnb-all"
              onClick={() => {
                setOpen(false);
                navigate(allLink);
              }}
            >
              See all notifications <FontAwesomeIcon icon={faArrowRight} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
